import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Collapse,
  IconButton,
  Alert,
  CircularProgress,
  List,
  ListItem,
} from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { OperationalUnitDto } from "../../store/models/operationalUnit/OperationalUnitDto";

interface OperationalUnitNode extends OperationalUnitDto {
  children: OperationalUnitNode[];
  level: number;
}

interface OperationalUnitsSelectorProps {
  operationalUnits: OperationalUnitDto[];
  selectedUnits: string[];
  onSelectionChange: (selectedUnits: string[]) => void;
  loading?: boolean;
  disabled?: boolean;
}

// Extracted comparison function to reduce nesting
const compareUnitsForSorting = (a: OperationalUnitNode, b: OperationalUnitNode): number => {
  // Sort by name only - tree structure is based on parentUnitNumber, not unitType
  return a.unitName.localeCompare(b.unitName);
};

// Extracted nested function to reduce nesting complexity
const findUnitInfoInTree = (
  unitNumber: string,
  tree: OperationalUnitNode[],
  operationalUnits: OperationalUnitDto[]
): { level: number; parentUnitNumber?: string } | null => {
  const findInTree = (nodes: OperationalUnitNode[]): { level: number; parentUnitNumber?: string } | null => {
    for (const node of nodes) {
      if (node.unitNumber === unitNumber) {
        return {
          level: node.level,
          parentUnitNumber: operationalUnits.find((u) => u.unitNumber === unitNumber)?.parentUnitNumber,
        };
      }
      const childResult = findInTree(node.children);
      if (childResult !== null) {
        return childResult;
      }
    }
    return null;
  };
  return findInTree(tree);
};

const OperationalUnitsSelector: React.FC<OperationalUnitsSelectorProps> = ({
                                                                             operationalUnits,
                                                                             selectedUnits,
                                                                             onSelectionChange,
                                                                             loading = false,
                                                                             disabled = false,
                                                                           }) => {
  const { t } = useTranslation();
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  // Auto-expand all nodes on mount
  useEffect(() => {
    if (operationalUnits.length > 0) {
      const allUnitNumbers = operationalUnits.map((unit) => unit.unitNumber);
      setExpandedNodes(new Set(allUnitNumbers));
    }
  }, [operationalUnits]);

  // Build hierarchical tree from flat array
  const tree = useMemo(() => {
    const nodeMap = new Map<string, OperationalUnitNode>();
    const roots: OperationalUnitNode[] = [];

    // Filter out disabled units
    const enabledUnits = operationalUnits.filter((unit) => unit.unitStatus === "ENABLED");

    // First pass: create all nodes
    enabledUnits.forEach((unit) => {
      nodeMap.set(unit.unitNumber, {
        ...unit,
        children: [],
        level: 0,
      });
    });

    // Second pass: build relationships
    enabledUnits.forEach((unit) => {
      const node = nodeMap.get(unit.unitNumber)!;

      if (unit.parentUnitNumber) {
        // parentUnitNumber is a unitNumber, not externalUnitNumber
        if (nodeMap.has(unit.parentUnitNumber)) {
          const parent = nodeMap.get(unit.parentUnitNumber)!;
          parent.children.push(node);
        } else {
          // Parent not found in enabled units, treat as root
          roots.push(node);
        }
      } else {
        roots.push(node);
      }
    });

    // Third pass: calculate levels correctly
    const calculateLevels = (nodes: OperationalUnitNode[], level: number) => {
      nodes.forEach((node) => {
        node.level = level;
        if (node.children.length > 0) {
          calculateLevels(node.children, level + 1);
        }
      });
    };

    calculateLevels(roots, 0);

    // Sort children by unitType hierarchy and name
    const sortChildren = (nodes: OperationalUnitNode[]) => {
      nodes.forEach((node) => {
        if (node.children.length > 0) {
          node.children.sort(compareUnitsForSorting);
          sortChildren(node.children);
        }
      });
    };

    sortChildren(roots);
    roots.sort((a, b) => a.unitName.localeCompare(b.unitName));

    return roots;
  }, [operationalUnits]);

  const getAllChildren = (unit: OperationalUnitNode): string[] => {
    const result: string[] = [];
    const childNumbers = unit.children.map(child => child.externalUnitNumber);
    const childrenRecursive = unit.children.flatMap(child => getAllChildren(child));
    result.push(...childNumbers, ...childrenRecursive);
    return result;
  };

  const getSelectedLevel = (): number | null => {
    if (selectedUnits.length === 0) return null;

    // Find the unit by external number first
    const firstSelectedUnit = operationalUnits.find((u) => u.externalUnitNumber === selectedUnits[0]);
    if (!firstSelectedUnit) return null;

    const unitInfo = findUnitInfoInTree(firstSelectedUnit.unitNumber, tree, operationalUnits);
    return unitInfo?.level ?? null;
  };

  const canSelectUnit = (unit: OperationalUnitNode): boolean => {
    // If disabled, no units can be selected
    if (disabled) return false;

    const selectedLevel = getSelectedLevel();
    if (selectedLevel === null) return true;

    return unit.level === selectedLevel;
  };

  const handleUnitToggle = (unit: OperationalUnitNode) => {
    // Prevent selection if unit is at a different level
    if (!canSelectUnit(unit)) return;

    const isSelected = selectedUnits.includes(unit.externalUnitNumber);
    let newSelection: string[];

    if (isSelected) {
      // Deselecting: remove this unit and all its children
      const childrenToRemove = [unit.externalUnitNumber, ...getAllChildren(unit)];
      newSelection = selectedUnits.filter((id) => !childrenToRemove.includes(id));
    } else {
      // Selecting: add only this unit (no children since they're at different level)
      newSelection = [...selectedUnits, unit.externalUnitNumber];
    }

    onSelectionChange(newSelection);
  };

  const handleExpandToggle = (unitNumber: string) => {
    if (disabled) return;

    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(unitNumber)) {
      newExpanded.delete(unitNumber);
    } else {
      newExpanded.add(unitNumber);
    }
    setExpandedNodes(newExpanded);
  };

  const isUnitSelected = (unit: OperationalUnitNode): boolean => {
    return selectedUnits.includes(unit.externalUnitNumber);
  };

  const isIndeterminate = (unit: OperationalUnitNode): boolean => {
    if (unit.children.length === 0) return false;

    const selectedChildren = unit.children.filter((child) => isUnitSelected(child));
    return selectedChildren.length > 0 && selectedChildren.length < unit.children.length;
  };

  const renderUnitNode = (unit: OperationalUnitNode, depth: number = 0): React.ReactNode => {
    const hasChildren = unit.children.length > 0;
    const isExpanded = expandedNodes.has(unit.unitNumber);
    const isSelected = isUnitSelected(unit);
    const indeterminate = isIndeterminate(unit);
    const canSelect = canSelectUnit(unit) && !disabled;

    return (
      <Box key={unit.unitNumber}>
        <ListItem
          sx={{
            pl: depth * 3 + 1,
            py: 0.5,
            bgcolor: isSelected ? "action.selected" : "transparent",
            opacity: canSelect ? 1 : 0.5,
          }}
        >
          {hasChildren && (
            <IconButton
              size="small"
              onClick={() => handleExpandToggle(unit.unitNumber)}
              sx={{ mr: 1, minWidth: "auto" }}
              disabled={disabled}
            >
              {isExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          )}
          {!hasChildren && <Box sx={{ width: 40 }} />}

          <FormControlLabel
            control={
              <Checkbox
                checked={isSelected}
                indeterminate={indeterminate}
                onChange={disabled ? undefined : () => handleUnitToggle(unit)}
                disabled={disabled || !canSelect}
                size="small"
                sx={{ ml: 0 }}
              />
            }
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" fontWeight="medium">
                  {unit.unitName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ({unit.externalUnitNumber})
                </Typography>
              </Box>
            }
            sx={{
              flex: 1,
              margin: 0,
              alignItems: "center",
              cursor: canSelect && !disabled ? "pointer" : "not-allowed",
              pointerEvents: disabled ? "none" : "auto",
            }}
            disabled={disabled || !canSelect}
          />
        </ListItem>

        {hasChildren && (
          <Collapse in={isExpanded}>
            <List dense disablePadding>
              {unit.children.map((child) => renderUnitNode(child, depth + 1))}
            </List>
          </Collapse>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (operationalUnits.length === 0) {
    return <Alert severity="info">{t("coupon.form.operationalUnits.noOperationalUnitsAvailable")}</Alert>;
  }

  return (
    <Box sx={{ opacity: disabled ? 0.6 : 1 }}>
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          maxHeight: operationalUnits.length > 15 ? "400px" : "auto",
          overflow: operationalUnits.length > 15 ? "auto" : "visible",
          bgcolor: "background.paper",
        }}
      >
        <List dense disablePadding>
          {tree.map((unit) => renderUnitNode(unit))}
        </List>
      </Box>
    </Box>
  );
};

export default OperationalUnitsSelector;
